import { useEffect, useState } from 'react';

// The remembered values for a key, on top of the defaults, so a filter added
// later still gets its default.
const readStored = (storageKey, defaults) => {
  try {
    const stored = sessionStorage.getItem(storageKey);
    if (stored) return { ...defaults, ...JSON.parse(stored) };
  } catch {
    /* ignore — fall through to defaults */
  }
  return defaults;
};

/**
 * Persist a small bag of UI filter values in sessionStorage so they survive
 * navigation away and back (and refresh) within the current tab.
 *
 * Cleared when the tab closes — admins don't get the localStorage surprise of
 * a filter still being set days later.
 *
 * Usage:
 *   const [filters, setFilter] = useSessionFilters('clubMembersFilters_123', {
 *     searchText: '',
 *     role: 'all',
 *     status: 'all',
 *   });
 *
 *   const { searchText, role, status } = filters;
 *   <input value={searchText} onChange={(e) => setFilter('searchText', e.target.value)} />
 *
 * Defaults are merged on top of any persisted state so adding a new filter
 * later doesn't break existing stored entries.
 *
 * Each key keeps its own values. When the key changes while the page stays
 * open (the club switched with the page open), the page reads the new key's
 * remembered values, or the defaults. It used to keep the old club's values and
 * save them under the new club (Doug, 2026-10-25: Redlands' events showed in
 * Import Speed School's Season Best Report).
 */
export default function useSessionFilters(storageKey, defaults) {
  // The values, and the key they belong to.
  const [state, setState] = useState(() => ({
    key: storageKey,
    filters: readStored(storageKey, defaults),
  }));

  // A new key: React redraws with its values before anything is shown.
  let current = state;
  if (state.key !== storageKey) {
    current = { key: storageKey, filters: readStored(storageKey, defaults) };
    setState(current);
  }

  useEffect(() => {
    try {
      sessionStorage.setItem(state.key, JSON.stringify(state.filters));
    } catch {
      /* sessionStorage can fail (private mode, quota); ignore */
    }
  }, [state]);

  const setFilter = (key, value) => {
    setState((prev) => ({ ...prev, filters: { ...prev.filters, [key]: value } }));
  };

  return [current.filters, setFilter];
}
